import { Button, Input, message, Modal, Typography } from "antd";
import { ApiService } from "api/api.service";
import { useEffect, useState } from "react";
import { TbInfoCircle } from "react-icons/tb";
import { z } from "zod";

export const AddEmailsModal = ({
  open,
  onCancel,
  appKey,
}: {
  open: boolean;
  onCancel: () => unknown;
  appKey?: string;
}) => {
  const [current, setCurrent] = useState("");
  const [emails, setEmails] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const appEmails = ApiService.application.useGetAdditionalEmails(appKey);
  const addEmails = ApiService.application.useAddAdditionalEmails();

  const generateHash = (emails: string[]) => {
    return emails.sort().join(";");
  };

  const verifyHash = () => {
    const currentHash = generateHash(emails);
    const existingHash = generateHash([
      ...(appEmails.data?.result.existing ?? []),
      ...(appEmails.data?.result.extra ?? []),
    ]);

    return currentHash === existingHash;
  };

  useEffect(() => {
    reset();
  }, [appEmails.data]);

  const reset = () => {
    setCurrent("");
    if (appEmails.data) {
      setEmails((prev) => [...appEmails.data.result.existing, ...appEmails.data.result.extra].sort());
    }
    setError(null);
    onCancel();
  };

  const verify = (text: string): string | null => {
    if (!z.string().email().safeParse(text).success) return "Please provide a valid email address";
    if (emails.includes(text)) {
      return "This mail is already selected";
    }

    return null;
  };

  const addEmail = (email: string) => {
    email = email.trim();
    const errorMessage = verify(email);

    if (errorMessage) {
      setError(errorMessage);
    } else {
      setEmails((prev) => [...prev, email]);
      setCurrent("");
    }
  };

  const removeEmail = (email: string) => {
    setEmails((prev) => prev.filter((e) => e !== email));
  };

  return (
    <Modal
      title="Add Additional Emails"
      onOk={async () => {
        await addEmails.mutateAsync(
          { appKey, emails: emails.filter((e) => !appEmails.data?.result.existing?.includes(e)) },
          {
            onSuccess(data) {
              setCurrent("");
              setEmails([]);
              let messageText = `Successfully `;
              if (data.result.inserted > 0) {
                messageText = messageText.concat(`added ${data.result.inserted} email(s)`);
              }

              if (data.result.deleted > 0) {
                if (data.result.inserted > 0) {
                  messageText = messageText.concat(" and ");
                }

                messageText = messageText.concat(`removed ${data.result.deleted} email(s)`);
              }

              messageText = messageText.concat(".");

              message.success(messageText);
              onCancel();
            },
            onSettled() {
              appEmails.refetch();
            },
          },
        );
      }}
      okButtonProps={{
        disabled: verifyHash(),
      }}
      open={open}
      onCancel={reset}
      okText={`Save Emails`}
    >
      <div className="flex flex-col gap-2 max-h-64 overflow-y-auto">
        {emails.map((e) => (
          <div key={e} className="flex gap-2">
            <Input value={e} readOnly />
            {!appEmails.data?.result.existing?.includes(e) && (
              <Button className="w-20" danger onClick={() => removeEmail(e)}>
                Remove
              </Button>
            )}
          </div>
        ))}
      </div>
      <div className="mt-2">
        <div className="flex gap-2">
          <Input
            placeholder="Enter creditor email"
            value={current}
            onChange={(e) => {
              const value = e.target.value;
              if (value === "") setError(null);

              const message = verify(value);
              setError(message);

              setCurrent(value);
            }}
          />
          <Button className="w-20" disabled={Boolean(verify(current))} onClick={() => addEmail(current)}>
            Add
          </Button>
        </div>
        <Typography.Text className="text-red-400 text-xs pl-1" hidden={!error || !current}>
          {/* <TbInfoCircle className="inline-block mr-1" /> */}
          {error}
        </Typography.Text>
      </div>
    </Modal>
  );
};
